import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Row = ({ label, value, rank, highlight }) => (
  <div className={`flex items-center justify-between py-1.5 border-b border-[rgba(101,141,113,0.2)] last:border-0 ${highlight ? 'bg-[rgba(216,206,179,0.04)]' : ''}`}>
    <span className="text-[10px] uppercase tracking-widest text-[#a9c9b1] font-bold">{label}</span>
    <div className="flex items-center gap-2">
      {rank && (
        <span className={`text-[9px] font-black px-1.5 py-0.5 ${rank <= 5 ? 'bg-[#386646] text-[#a9c9b1]' : rank >= 25 ? 'bg-[rgba(196,51,62,0.3)] text-[#e6a8a8]' : 'bg-[rgba(0,0,0,0.3)] text-[#658d71]'}`}>
          #{rank} MLB
        </span>
      )}
      <span className="text-sm font-black text-white tabular-nums">{value ?? '--'}</span>
    </div>
  </div>
);

const SeasonTeamStats = ({ teamStats }) => {
  const [view, setView] = useState('bat');

  const hitting = teamStats?.stats?.find(s => s.group?.displayName === 'hitting')?.splits?.[0]?.stat;
  const pitching = teamStats?.stats?.find(s => s.group?.displayName === 'pitching')?.splits?.[0]?.stat;

  return (
    <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)] flex flex-col">
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
        <span className="text-xs font-black uppercase tracking-widest text-[#d8ceb3]">Team Stats</span>
        <div className="flex gap-1">
          {['bat', 'pit'].map(v => (
            <button key={v} onClick={() => setView(v)}
              className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 border transition-colors ${view === v ? 'bg-[#658d71] border-[#a9c9b1] text-white' : 'border-[#658d71] text-[#a9c9b1] hover:text-white'}`}>
              {v === 'bat' ? 'Batting' : 'Pitching'}
            </button>
          ))}
        </div>
      </div>
      <div className="p-3">
        <AnimatePresence mode="wait">
          <motion.div key={view} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.12 }}>
            {view === 'bat' && hitting ? (
              <>
                <Row label="Batting Avg" value={hitting.avg} highlight />
                <Row label="On-Base %" value={hitting.obp} />
                <Row label="Slugging" value={hitting.slg} />
                <Row label="OPS" value={hitting.ops} highlight />
                <Row label="Home Runs" value={hitting.homeRuns} />
                <Row label="RBI" value={hitting.rbi} />
                <Row label="Stolen Bases" value={hitting.stolenBases} />
                <Row label="Strikeouts" value={hitting.strikeOuts} />
                <Row label="Walks" value={hitting.baseOnBalls} />
                <Row label="Runs Scored" value={hitting.runs} highlight />
              </>
            ) : view === 'bat' ? (
              <p className="text-[#658d71] text-xs text-center py-6 uppercase tracking-widest">No batting data</p>
            ) : null}
            {view === 'pit' && pitching ? (
              <>
                <Row label="Team ERA" value={pitching.era} highlight />
                <Row label="WHIP" value={pitching.whip} />
                <Row label="Strikeouts" value={pitching.strikeOuts} />
                <Row label="Walks" value={pitching.baseOnBalls} />
                <Row label="Saves" value={pitching.saves} />
                <Row label="Holds" value={pitching.holds} />
                <Row label="Innings Pitched" value={pitching.inningsPitched} />
                <Row label="Hits Allowed" value={pitching.hits} highlight />
                <Row label="HR Allowed" value={pitching.homeRuns} />
                <Row label="Opp. Avg" value={pitching.avg} />
              </>
            ) : view === 'pit' ? (
              <p className="text-[#658d71] text-xs text-center py-6 uppercase tracking-widest">No pitching data</p>
            ) : null}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SeasonTeamStats;